import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// components
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
// types
import { setError } from 'src/redux/slices/error';
import { AccountingSettingsService, AccountingSettingsDto } from '../../../api';
import { useLocales } from '../../../locales';
import { useDispatch } from '../../../redux/store';

// ----------------------------------------------------------------------

type Props = {
  settings: AccountingSettingsDto;
};

export default function BookkeepingEditForm({ settings }: Props) {
  const mdUp = useResponsive('up', 'md');

  const { t } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const newSchema = Yup.object().shape({
    // chainName: Yup.string().required(t('Name is required')),
    salesWithVat: Yup.lazy((value) => (value === '' ? Yup.string() : Yup.number().min(0))),
    salesWithoutVat: Yup.lazy((value) => (value === '' ? Yup.string() : Yup.number().min(0))),
    paymentProvider: Yup.lazy((value) => (value === '' ? Yup.string() : Yup.number().min(0))),
    bank: Yup.lazy((value) => (value === '' ? Yup.string() : Yup.number().min(0))),
    fees: Yup.lazy((value) => (value === '' ? Yup.string() : Yup.number().min(0))),
    cash: Yup.lazy((value) => (value === '' ? Yup.string() : Yup.number().min(0))),
    check: Yup.lazy((value) => (value === '' ? Yup.string() : Yup.number().min(0))),
    mobilePay: Yup.lazy((value) => (value === '' ? Yup.string() : Yup.number().min(0))),
    newVatName: Yup.string(),
    newVatRate: Yup.number(),
    newVatAccount: Yup.number(),
  });

  const defaultValues = useMemo(
    () => ({
      // chainName: settings?.chainName ?? '',
      salesWithVat: settings?.salesWithVat ?? 1000,
      salesWithoutVat: settings?.salesWithoutVat ?? 1100,
      paymentProvider: settings?.paymentProvider ?? 5050,
      bank: settings?.bank ?? 5000,
      fees: settings?.fees ?? 2400,
      cash: settings?.cash ?? 5000,
      check: settings?.check ?? 5000,
      mobilePay: settings?.mobilePay ?? 5000,
      newVatName: undefined,
      newVatRate: undefined,
      newVatAccount: undefined,
    }),
    [settings]
  );

  const methods = useForm({
    resolver: yupResolver(newSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await AccountingSettingsService.update({
        id: settings!.chainId!,
        body: {
          ...data,
        } as any,
      });

      enqueueSnackbar(t('Update success!'));
    } catch (error) {
      dispatch(setError(error));
    }
  });

  const dispatch = useDispatch();

  const renderAccounting = (
    <>
      {mdUp && (
        <Grid size={{ md: 4 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {t('Chart of accounts')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t(
              'Manage your financial accounts efficiently with these settings. Enter account numbers for different payment methods to streamline transactions and accounting processes. Specify accounts for sales with VAT, sales without VAT, fees, bank transactions, credit card payments, cash payments, check payments, and MobilePay transactions as needed.'
            )}
          </Typography>
        </Grid>
      )}

      <Grid size={{ xs: 12, md: 8 }}>
        <Card>
          {!mdUp && <CardHeader title={t('Accounting')} />}

          <Stack spacing={3} sx={{ p: 3 }}>
            <RHFTextField name="salesWithVat" label={t('Account number for sales w/ VAT')} />

            <RHFTextField name="salesWithoutVat" label={t('Account number for sales w/o VAT')} />

            <RHFTextField name="fees" label={t('Account number for fees')} />

            <RHFTextField name="bank" label={t('Account number for bank')} />

            <RHFTextField
              name="paymentProvider"
              label={t('Account number for credit card payment')}
            />

            <RHFTextField name="cash" label={t('Account number for cash payment')} />

            <RHFTextField name="check" label={t('Account number for check payment')} />

            <RHFTextField name="mobilePay" label={t('Account number for MobilePay payment')} />
          </Stack>
        </Card>
      </Grid>
    </>
  );

  const renderActions = (
    <>
      {mdUp && <Grid size={{ md: 4 }} />}
      <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flexGrow: 1, pl: 3 }} />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          loading={isSubmitting}
        >
          {t('Save Changes')}
        </Button>
      </Grid>
    </>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        {renderAccounting}

        {renderActions}
      </Grid>
    </FormProvider>
  );
}
